import { useState, useEffect, useCallback, useRef } from 'react';

type NetworkState = 'online' | 'degraded' | 'offline';

interface NetworkStatus {
  state: NetworkState;
  message: string | null;
  lastChecked: Date | null;
  checkNow: () => Promise<void>;
}

const SUPABASE_URL = 'https://quidbuzjosjewriszapj.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InF1aWRidXpqb3NqZXdyaXN6YXBqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTUwNTQ2MTksImV4cCI6MjA3MDYzMDYxOX0.sULaqVfSKlgmdensRRuS2GSH4Sb5MXBEmHUitNrtI4Q';
const PROBE_INTERVAL_MS = 120_000; // 2 minutes
const PROBE_TIMEOUT_MS = 12_000;
// Require multiple consecutive failures before showing the banner — a single
// transient probe failure (extension, ad blocker, brief blip) shouldn't alarm users.
const FAILURE_THRESHOLD = 3;

export function useNetworkStatus(): NetworkStatus {
  const [state, setState] = useState<NetworkState>('online');
  const [message, setMessage] = useState<string | null>(null);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);
  const failureCountRef = useRef(0);

  const markHealthy = useCallback(() => {
    failureCountRef.current = 0;
    setState('online');
    setMessage(null);
  }, []);

  const markUnhealthy = useCallback((nextState: 'degraded' | 'offline', msg: string) => {
    failureCountRef.current += 1;
    if (failureCountRef.current >= FAILURE_THRESHOLD) {
      setState(nextState);
      setMessage(msg);
    }
  }, []);

  const probe = useCallback(async () => {
    if (!mountedRef.current) return;

    // Skip probing when tab is hidden to avoid background request waste
    if (document.hidden) return;

    // Quick bail if browser is offline
    if (!navigator.onLine) {
      setState('offline');
      setMessage('You appear to be offline. Check your internet connection.');
      setLastChecked(new Date());
      return;
    }

    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);

      const res = await fetch(`${SUPABASE_URL}/auth/v1/health`, {
        method: 'GET',
        headers: { apikey: SUPABASE_ANON_KEY },
        signal: controller.signal,
        cache: 'no-store',
      });
      clearTimeout(timeout);

      if (!mountedRef.current) return;

      // Any HTTP response — even 4xx/5xx — proves connectivity is working.
      // Only flag 5xx as "degraded" after repeated failures.
      if (res.status >= 500) {
        markUnhealthy('degraded', 'Our servers are experiencing issues. Your requests will automatically retry.');
      } else {
        markHealthy();
      }
    } catch (err: any) {
      if (!mountedRef.current) return;

      if (err?.name === 'AbortError') {
        markUnhealthy('degraded', 'Connection is very slow. This may be due to your network\'s security settings. Try switching to mobile data or a different WiFi network.');
      } else {
        markUnhealthy('offline', 'Unable to reach our servers. Your network may be blocking this connection. Try a different network or disable your VPN.');
      }
    }

    if (mountedRef.current) {
      setLastChecked(new Date());
    }
  }, [markHealthy, markUnhealthy]);

  useEffect(() => {
    mountedRef.current = true;

    // Initial probe after short delay
    const initialTimeout = setTimeout(probe, 2000);

    // Periodic probing
    intervalRef.current = setInterval(probe, PROBE_INTERVAL_MS);

    // Browser online/offline events
    const handleOnline = () => probe();
    const handleOffline = () => {
      setState('offline');
      setMessage('You appear to be offline. Check your internet connection.');
      setLastChecked(new Date());
    };

    // Re-probe when tab becomes visible again
    const handleVisibility = () => {
      if (!document.hidden) probe();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      mountedRef.current = false;
      clearTimeout(initialTimeout);
      if (intervalRef.current) clearInterval(intervalRef.current);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [probe]);

  return { state, message, lastChecked, checkNow: probe };
}
